import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { contactsService, DuplicateCluster } from '@/services/contacts.service';
import { Users, ShieldCheck, Phone, Mail, ArrowRight, Search } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

/**
 * Duplicate-detection + merge wizard. Surfaces clusters of contacts that
 * share the same phone or email; user picks the primary in each cluster,
 * the rest fold into it. Idempotent — already-merged contacts stop appearing
 * after refresh.
 */
export default function ContactDuplicates() {
  const [clusters, setClusters] = useState<DuplicateCluster[] | null>(null);
  const [merging, setMerging] = useState<string | null>(null); // matchKey of cluster being merged
  const [primaryByCluster, setPrimaryByCluster] = useState<Record<string, string>>({});

  const refresh = async () => {
    setClusters(null);
    try {
      const r = await contactsService.duplicates(200);
      setClusters(r);
      // Default-pick the contact with the most references as primary — usually
      // the one we want to keep since it has the deepest history.
      const def: Record<string, string> = {};
      for (const c of r) {
        const ranked = [...c.contacts].sort((a, b) => {
          const refs = (x: typeof a) => (x.leadCount ?? 0) + (x.dealCount ?? 0) + (x.membershipCount ?? 0);
          if (refs(b) !== refs(a)) return refs(b) - refs(a);
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });
        def[c.matchKey] = ranked[0].contactUid;
      }
      setPrimaryByCluster(def);
    } catch {
      setClusters([]);
    }
  };

  useEffect(() => { refresh(); }, []);

  const merge = async (cluster: DuplicateCluster) => {
    const primaryUid = primaryByCluster[cluster.matchKey];
    if (!primaryUid) { toast.error('Pick a primary contact first'); return; }
    const otherUids = cluster.contacts.map((c) => c.contactUid).filter((uid) => uid !== primaryUid);
    if (otherUids.length === 0) { toast.error('Cluster has only one contact — nothing to merge'); return; }
    setMerging(cluster.matchKey);
    try {
      const r = await contactsService.merge(primaryUid, otherUids);
      const moved = r.moved;
      toast.success(
        `Merged ${r.mergedCount} contact(s). Moved: ${moved.leads}L · ${moved.deals}D · ${moved.memberships}G`,
      );
      // Optimistically drop the cluster from the list — it's resolved.
      setClusters((prev) => prev?.filter((c) => c.matchKey !== cluster.matchKey) ?? null);
    } catch { /* axios toasts */ } finally { setMerging(null); }
  };

  return (
    <div>
      <Topbar
        title="Duplicate contacts"
        breadcrumb="Contacts"
        description="Contacts that share the same phone or email. Pick the keeper, then merge — leads, deals, group memberships, documents, calendar events, and custom records all transfer."
        right={
          <div className="flex items-center gap-2">
            <Link to="/contacts" className="text-xs font-semibold text-text-muted hover:text-primary">
              Back to contacts
            </Link>
            <Button variant="secondary" onClick={refresh}>
              <Search size={14} /> Re-scan
            </Button>
          </div>
        }
      />

      <div className="w-full px-7 py-7 space-y-4">
        {clusters === null && <Card><div className="skeleton h-24 rounded-lg" /></Card>}

        {clusters && clusters.length === 0 && (
          <EmptyState
            icon={<ShieldCheck size={40} className="text-success" />}
            title="No duplicates found"
            description="All your contacts have unique phone numbers and email addresses. The next CSV import will be re-scanned."
          />
        )}

        {clusters && clusters.length > 0 && (
          <>
            <div className="rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm">
              <strong>{clusters.length} duplicate cluster{clusters.length === 1 ? '' : 's'} found.</strong>{' '}
              Pick the contact to keep in each cluster, then click Merge. The others soft-delete with their
              references reassigned to the primary — no data loss, the audit log keeps the trail.
            </div>

            {clusters.map((cluster) => (
              <Card key={cluster.matchKey} padded={false}>
                <div className="flex items-center gap-2 border-b border-border bg-surface-alt/40 px-4 py-2.5">
                  <span className="rounded-full bg-warning-soft px-2 py-0.5 text-2xs font-semibold uppercase text-warning">
                    {cluster.matchKind === 'phone' ? <Phone size={10} className="inline mr-0.5" /> : <Mail size={10} className="inline mr-0.5" />}
                    {cluster.matchKind} match
                  </span>
                  <span className="font-mono text-xs text-text-secondary">{cluster.matchKey}</span>
                  <span className="ml-auto text-xs text-text-muted">{cluster.contacts.length} contacts</span>
                </div>

                <div className="divide-y divide-border">
                  {cluster.contacts.map((c) => {
                    const isPrimary = primaryByCluster[cluster.matchKey] === c.contactUid;
                    return (
                      <label
                        key={c.contactUid}
                        className={clsx(
                          'flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors',
                          isPrimary ? 'bg-success-soft/40' : 'hover:bg-surface-alt',
                        )}
                      >
                        <input
                          type="radio"
                          name={`primary-${cluster.matchKey}`}
                          className="accent-primary"
                          checked={isPrimary}
                          onChange={() => setPrimaryByCluster((prev) => ({ ...prev, [cluster.matchKey]: c.contactUid }))}
                        />
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-text-primary">
                          <Users size={16} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate font-medium text-text-primary">
                              {[c.firstName, c.lastName].filter(Boolean).join(' ') || '(no name)'}
                            </span>
                            {isPrimary && (
                              <span className="rounded-full bg-success-soft px-2 py-0.5 text-2xs font-semibold text-success">
                                Keeper
                              </span>
                            )}
                            <Link
                              to={`/contacts/${c.contactUid}`}
                              onClick={(e) => e.stopPropagation()}
                              target="_blank"
                              rel="noreferrer"
                              className="text-2xs text-info hover:underline"
                            >
                              open ↗
                            </Link>
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-3 text-2xs text-text-muted">
                            {c.phone && <span><Phone size={10} className="inline mr-0.5" />{c.phone}</span>}
                            {c.email && <span><Mail size={10} className="inline mr-0.5" />{c.email}</span>}
                            <span>created {new Date(c.createdAt).toLocaleDateString()}</span>
                            <span className="font-mono">{c.contactUid.slice(0, 8)}…</span>
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end text-2xs text-text-muted">
                          {(c.leadCount ?? 0) > 0 && <span>{c.leadCount} leads</span>}
                          {(c.dealCount ?? 0) > 0 && <span>{c.dealCount} deals</span>}
                          {(c.membershipCount ?? 0) > 0 && <span>{c.membershipCount} groups</span>}
                          {!c.leadCount && !c.dealCount && !c.membershipCount && <span>no refs</span>}
                        </div>
                      </label>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-border bg-surface-alt/40 px-4 py-2.5">
                  <span className="text-2xs text-text-muted">
                    The non-keeper{cluster.contacts.length === 2 ? '' : 's'} will fold into the keeper. Soft-delete is reversible by an admin.
                  </span>
                  <Button
                    size="sm"
                    onClick={() => merge(cluster)}
                    loading={merging === cluster.matchKey}
                    disabled={!primaryByCluster[cluster.matchKey]}
                  >
                    Merge {cluster.contacts.length - 1} into keeper <ArrowRight size={12} />
                  </Button>
                </div>
              </Card>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
